import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import CountryDropdown from "~/components/countryDropDown";
import CustomeButton from "~/components/Button";
import { countryPhoneCodes } from "~/utils/countries";
import apiService from "~/services/service";
import API_ROUTES from "~/api-routes";
import type {
  NewAccountManagerData,
  AddAccountManagerModalProps,
  AccountManagerFormErrors,
} from "~/Pages/Tenants/types/tenants";

// Re-export for backward compatibility
export type { NewAccountManagerData } from "~/Pages/Tenants/types/tenants";

const initialFormData: NewAccountManagerData = {
  given_name: "",
  surname: "",
  other_name: "",
  preferred_name: "",
  country: "",
  email: "",
  phone_code: "",
  phone_number: "",
};

// Validation patterns
const NAME_PATTERN = /^[A-Za-z\s]+$/;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const DIGITS_ONLY_PATTERN = /^[0-9]+$/;

export const AddAccountManagerModal: React.FC<AddAccountManagerModalProps> = ({
  visible,
  onHide,
  onSubmit,
  isLoading = false,
  existingEmails = [],
}) => {
  const [formData, setFormData] =
    useState<NewAccountManagerData>(initialFormData);
  const [errors, setErrors] = useState<AccountManagerFormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExistsOnServer, setEmailExistsOnServer] = useState(false);
  const emailCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      setFormData(initialFormData);
      setErrors({});
      setTouched(new Set());
      setEmailExistsOnServer(false);
    }
  }, [visible]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
  }, []);

  // Check if email exists on server (debounced)
  const checkEmailExists = useCallback(async (email: string) => {
    if (!email.trim() || !EMAIL_PATTERN.test(email)) {
      setEmailExistsOnServer(false);
      return;
    }

    // Clear any pending timeout
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }

    // Debounce the API call
    emailCheckTimeoutRef.current = setTimeout(async () => {
      setIsCheckingEmail(true);
      try {
        const response: any = await apiService(
          API_ROUTES.FETCH_ACCOUNT_MANAGERS,
          "GET",
        );
        if (response?.data) {
          const emailExists = response.data.some(
            (manager: any) =>
              manager.email?.toLowerCase() === email.toLowerCase(),
          );
          setEmailExistsOnServer(emailExists);
          if (emailExists) {
            setErrors((prev) => ({
              ...prev,
              email: "This email already exists",
            }));
          }
        }
      } catch (error) {
        console.error("Failed to check email:", error);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500); // 500ms debounce
  }, []);

  const validateField = (
    field: keyof NewAccountManagerData,
    value: string,
  ): string | undefined => {
    switch (field) {
      case "given_name":
        if (!value.trim()) return "Given name is required";
        if (!NAME_PATTERN.test(value))
          return "Special characters are not allowed";
        break;
      case "surname":
      case "other_name":
      case "preferred_name":
        if (value.trim() && !NAME_PATTERN.test(value))
          return "Special characters are not allowed";
        break;
      case "country":
        if (!value) return "Country is required";
        break;
      case "email":
        if (!value.trim()) return "Contact email is required";
        if (!EMAIL_PATTERN.test(value)) return "Email is invalid";
        if (existingEmails.includes(value.toLowerCase()) || emailExistsOnServer)
          return "This email already exists";
        break;
      case "phone_code":
        // Phone code comes from dropdown, no validation needed
        break;
      case "phone_number":
        if (value.trim() && !DIGITS_ONLY_PATTERN.test(value))
          return "Phone number must contain only digits";
        break;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: AccountManagerFormErrors = {};
    (Object.keys(formData) as Array<keyof NewAccountManagerData>).forEach(
      (field) => {
        const error = validateField(field, formData[field]);
        if (error) newErrors[field] = error;
      },
    );
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (
    field: keyof NewAccountManagerData,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Validate on change if field was touched
    if (touched.has(field)) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
    // Check email existence on server when email changes
    if (field === "email") {
      setEmailExistsOnServer(false); // Reset server check state
      checkEmailExists(value);
    }
  };

  const handleFieldBlur = (field: keyof NewAccountManagerData) => {
    setTouched((prev) => new Set(prev).add(field));
    const error = validateField(field, formData[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
    // Also trigger server-side email check on blur
    if (field === "email") {
      checkEmailExists(formData.email);
    }
  };

  const handleSubmit = () => {
    // Mark all fields as touched
    setTouched(new Set(Object.keys(formData)));
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setErrors({});
    setTouched(new Set());
    onHide();
  };

  // Check if required fields are filled and no validation errors
  const hasRequiredFields =
    formData.given_name && formData.country && formData.email;
  const hasNoErrors = Object.keys(errors).every(
    (key) => !errors[key as keyof AccountManagerFormErrors],
  );
  const isFormValid =
    hasRequiredFields &&
    hasNoErrors &&
    !isCheckingEmail &&
    !emailExistsOnServer;

  const footerContent = (
    <div className="flex justify-content-end gap-2">
      <CustomeButton
        label="Cancel"
        variant="outlined"
        onClick={handleCancel}
        disabled={isLoading}
        type="button"
      />
      <CustomeButton
        label="Add Account Manager"
        variant="contained"
        onClick={handleSubmit}
        disabled={!isFormValid || isLoading}
        loading={isLoading}
        type="button"
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleCancel}
      className="add-account-manager-modal"
      footer={footerContent}
      draggable={false}
      closable={!isLoading}
    >
      <div className="flex flex-column gap-3">
        <div className="header flex flex-column gap-2">
          <p className="title m-0">Add New Account Manager</p>
          <p className="description m-0">
            Fill in the new contacts information below and hit add contact when
            ready!
          </p>
        </div>

        <div className="formgrid grid">
          {/* Given name and Other name */}
          <div className="field col-6">
            <label htmlFor="given_name" className="input-label mb-2">
              Given name(s) *{" "}
              <i className="pi pi-info-circle given-name-tooltip" />
              <Tooltip
                target=".given-name-tooltip"
                content="First name or given names"
              />
            </label>
            <InputText
              id="given_name"
              value={formData.given_name}
              onChange={(e) => handleFieldChange("given_name", e.target.value)}
              onBlur={() => handleFieldBlur("given_name")}
              placeholder="Enter given name(s)"
              className={`w-full ${errors.given_name ? "p-invalid" : ""}`}
              disabled={isLoading}
            />
            {errors.given_name && (
              <small className="p-error">{errors.given_name}</small>
            )}
          </div>
          <div className="field col-6">
            <label htmlFor="other_name" className="input-label mb-2">
              Other name(s){" "}
              <i className="pi pi-info-circle other-name-tooltip" />
              <Tooltip
                target=".other-name-tooltip"
                content="Middle names or clan names"
              />
            </label>
            <InputText
              id="other_name"
              value={formData.other_name}
              onChange={(e) => handleFieldChange("other_name", e.target.value)}
              onBlur={() => handleFieldBlur("other_name")}
              placeholder="Enter any other n..."
              className={`w-full ${errors.other_name ? "p-invalid" : ""}`}
              disabled={isLoading}
            />
            {errors.other_name && (
              <small className="p-error">{errors.other_name}</small>
            )}
          </div>

          {/* Surname and Preferred name */}
          <div className="field col-6">
            <label htmlFor="surname" className="input-label mb-2">
              Surname(s) <i className="pi pi-info-circle surname-tooltip" />
              <Tooltip
                target=".surname-tooltip"
                content="Family name or surname"
              />
            </label>
            <InputText
              id="surname"
              value={formData.surname}
              onChange={(e) => handleFieldChange("surname", e.target.value)}
              onBlur={() => handleFieldBlur("surname")}
              placeholder="Enter surname(s)."
              className={`w-full ${errors.surname ? "p-invalid" : ""}`}
              disabled={isLoading}
            />
            {errors.surname && (
              <small className="p-error">{errors.surname}</small>
            )}
          </div>
          <div className="field col-6">
            <label htmlFor="preferred_name" className="input-label mb-2">
              Preferred name(s)
            </label>
            <InputText
              id="preferred_name"
              value={formData.preferred_name}
              onChange={(e) =>
                handleFieldChange("preferred_name", e.target.value)
              }
              onBlur={() => handleFieldBlur("preferred_name")}
              placeholder="Preferred name or nickname"
              className={`w-full ${errors.preferred_name ? "p-invalid" : ""}`}
              disabled={isLoading}
            />
            {errors.preferred_name && (
              <small className="p-error">{errors.preferred_name}</small>
            )}
          </div>

          {/* Country */}
          <div className="field col-12">
            <label htmlFor="country" className="input-label mb-2">
              Country *
            </label>
            <CountryDropdown
              id="country"
              value={formData.country}
              onChange={(e: any) =>
                handleFieldChange("country", e.value?.code || e.value)
              }
              onBlur={() => handleFieldBlur("country")}
              placeholder="Select country"
              className={`w-full ${errors.country ? "p-invalid" : ""}`}
              disabled={isLoading}
            />
            {errors.country && (
              <small className="p-error">{errors.country}</small>
            )}
          </div>

          {/* Contact email */}
          <div className="field col-12">
            <label htmlFor="email" className="input-label mb-2">
              Contact email *
            </label>
            <div style={{ position: "relative" }}>
              <InputText
                id="email"
                value={formData.email}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                onBlur={() => handleFieldBlur("email")}
                placeholder="Enter email"
                className={`w-full ${errors.email ? "p-invalid" : ""}`}
                type="email"
                disabled={isLoading}
              />
              {isCheckingEmail && (
                <i
                  className="pi pi-spin pi-spinner"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "0",
                    bottom: "0",
                    margin: "auto",
                    height: "fit-content",
                    color: "var(--text-color-secondary)",
                  }}
                />
              )}
            </div>
            {errors.email && <small className="p-error">{errors.email}</small>}
          </div>

          {/* Phone number */}
          <div className="field col-12">
            <label className="input-label mb-2">Phone number</label>
            <div className="flex gap-2">
              <Dropdown
                id="phone_code"
                value={formData.phone_code || null}
                onChange={(e) => handleFieldChange("phone_code", e.value || "")}
                onBlur={() => handleFieldBlur("phone_code")}
                options={countryPhoneCodes.map((item) => ({
                  label: `${item.dial_code} ${item.name}`,
                  value: item.dial_code.replace("+", ""),
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder="Country code"
                className={`w-full ${errors.phone_code ? "p-invalid" : ""}`}
                disabled={isLoading}
                filter
                appendTo={document.body}
              />
              <InputText
                id="phone_number"
                value={formData.phone_number}
                onChange={(e) =>
                  handleFieldChange("phone_number", e.target.value)
                }
                onBlur={() => handleFieldBlur("phone_number")}
                placeholder="Enter phone number"
                className={`w-full ${errors.phone_number ? "p-invalid" : ""}`}
                disabled={isLoading}
              />
            </div>
            {(errors.phone_code || errors.phone_number) && (
              <small className="p-error">
                {errors.phone_code || errors.phone_number}
              </small>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
